<template>
    <div>
        <x-layout class="question-set">
            <x-flex xs2 offset-xs1>
                <h3 class="question-set-title">Policy</h3>
            </x-flex>

            <x-flex xs6 class="question-set-questions">
                <x-container>
                    <x-layout v-if="!$root.userIsPlacingBrokerUser()">
                        <x-flex xs6>
                            <x-select
                                v-model="submission.placing_broker_company_id"
                                :items="placing_brokers"
                                class="required"
                                label="Choose placing broker"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Broker')]"
                                append-icon="keyboard_arrow_down"
                                item-value="id"
                                item-text="name"
                                outline
                                required
                                :menu-props="{ offsetY: true }"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="!$root.userIsClientUser()">
                        <x-flex xs6>
                            <x-autocomplete
                                v-model="selectedInsured"
                                :items="insureds"
                                class="required"
                                label="Choose insured"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Insured')]"
                                append-icon="keyboard_arrow_down"
                                item-value="id"
                                item-text="name"
                                outline
                                required
                                :menu-props="{ offsetY: true }"
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-text-field
                                v-if="selectedInsured"
                                v-bind:key="submission.client_company.id"
                                v-model="line1"
                                :label="`${submission.client_company.name} address`"
                                placeholder=" "
                                item-text="name"
                                outline
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout wrap>
                        <x-flex xs6>
                            <x-autocomplete
                                v-model="selectedEntities"
                                :items="insuredEntities"
                                class="required"
                                label="Choose entities"
                                placeholder=" "
                                :rules="[
                                    v => ruleRequired(v, 'Entity'),
                                    v => ruleMinSelected(v, 1, false, 'At least 1 entity must be selected')
                                ]"
                                append-icon="keyboard_arrow_down"
                                item-value="id"
                                item-text="name"
                                outline
                                multiple
                                required
                            >
                                <template slot="selection" slot-scope="{ item, index }">
                                    <span v-if="selectedEntities.length === 1">
                                        {{ item.name }}
                                    </span>
                                    <span v-else-if="selectedEntities.length > 1">
                                        {{ selectedEntities.length }} entities selected
                                    </span>
                                </template>
                            </x-autocomplete>
                        </x-flex>

                        <x-flex v-for="(selectedEntityAddress, index) in submission.entities" :key="index" xs6>
                            <x-text-field
                                v-bind:key="selectedEntityAddress.entity_id"
                                v-model="selectedEntityAddress.address.line1"
                                :label="`${selectedEntityAddress.name} address`"
                                placeholder=" "
                                outline
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs6>
                            <x-autocomplete
                                v-if="!isAmendment"
                                v-model="submission.placing_broker_contact_id"
                                :items="clientAssignments"
                                class="required"
                                label="Placing broker contact"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Placing broker contact')]"
                                append-icon="keyboard_arrow_down"
                                item-value="broker_user_id"
                                item-text="user_name"
                                outline
                                required
                                :menu-props="{ offsetY: true }"
                            />
                        </x-flex>

                        <x-flex xs6>
                            <x-select
                                v-if="!$root.userIsProducingBrokerUser()"
                                v-model="submission.producing_broker_company_id"
                                :items="producing_brokers"
                                class="required"
                                label="Choose broker"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Broker')]"
                                append-icon="keyboard_arrow_down"
                                item-value="id"
                                item-text="name"
                                outline
                                required
                                :menu-props="{ offsetY: true }"
                            />
                        </x-flex>
                    </x-layout>

                    <template v-if="submission.producing_broker_company_id && selectedInsured">
                        <x-layout v-if="!$root.userIsProducingBrokerUser()">
                            <x-flex xs6>
                                <x-text-field
                                    v-model="submission.broker_address.line1"
                                    label="Broker address"
                                    placeholder=" "
                                    outline
                                />
                            </x-flex>

                            <x-flex xs6>
                                <x-autocomplete
                                    v-model="submission.broker_address.country_id"
                                    :items="countries"
                                    label="Broker country"
                                    placeholder=" "
                                    append-icon="keyboard_arrow_down"
                                    item-value="id"
                                    item-text="name"
                                    outline
                                    :menu-props="{ offsetY: true }"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout v-if="!isAmendment">
                            <x-flex xs6>
                                <x-autocomplete
                                    v-model="submission.category_id"
                                    :items="categories"
                                    class="required"
                                    label="Choose Category"
                                    placeholder=" "
                                    :rules="[v => ruleRequired(v, 'Category')]"
                                    append-icon="keyboard_arrow_down"
                                    item-value="id"
                                    item-text="name"
                                    outline
                                    required
                                    :menu-props="{ offsetY: true }"
                                />
                            </x-flex>

                            <x-flex xs6>
                                <x-autocomplete
                                    v-model="submission.product_id"
                                    :items="filteredProducts"
                                    class="required"
                                    label="Choose Product"
                                    placeholder=" "
                                    :rules="[v => ruleRequired(v, 'Product')]"
                                    append-icon="keyboard_arrow_down"
                                    item-value="id"
                                    item-text="name"
                                    outline
                                    required
                                    :menu-props="{ offsetY: true }"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs6>
                                <x-autocomplete
                                    v-model="submission.currency_id"
                                    :items="currencies"
                                    class="required"
                                    label="Choose Currency"
                                    placeholder=" "
                                    :rules="[v => ruleRequired(v, 'Currency')]"
                                    append-icon="keyboard_arrow_down"
                                    item-value="id"
                                    item-text="name"
                                    outline
                                    required
                                    :menu-props="{ offsetY: true }"
                                />
                            </x-flex>
                        </x-layout>
                    </template>
                </x-container>
            </x-flex>
        </x-layout>

        <template v-if="submission.producing_broker_company_id && selectedInsured">
            <x-layout class="question-set obligors">
                <x-flex xs2 offset-xs1>
                    <h3 class="question-set-title">Obligors</h3>
                </x-flex>

                <x-flex xs6 class="question-set-questions">
                    <x-container>
                        <x-layout>
                            <x-flex xs12>
                                <x-radio-group
                                    v-model="showObligor"
                                    :row="true"
                                    class="required"
                                    :rules="[
                                        v => ruleRequired(v, 'Obligor')
                                    ]"
                                >
                                    <template v-slot:label>
                                        Add obligor?
                                    </template>

                                    <x-radio label="Yes" :value="true" />

                                    <x-radio label="No" :value="false" />
                                </x-radio-group>
                            </x-flex>
                        </x-layout>

                        <template v-if="showObligor">
                            <x-layout v-for="(obligor, index) in selectedObligors" :key="index">
                                <x-flex>
                                    <x-autocomplete
                                        v-model="obligor.country_id"
                                        :items="countries"
                                        class="required"
                                        label="Obligor country"
                                        placeholder="Country"
                                        item-value="id"
                                        item-text="name"
                                        :rules="[v => ruleRequired(v, 'Country')]"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                        required
                                        :menu-props="{ offsetY: true }"
                                    />
                                </x-flex>

                                <x-flex>
                                    <x-autocomplete
                                        v-model="obligor.pivot.obligor_id"
                                        :items="filteredObligors[index]"
                                        class="required"
                                        label="Obligor"
                                        placeholder="Obligor"
                                        item-value="id"
                                        item-text="name"
                                        :rules="[v => ruleRequired(v, 'Obligor')]"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                    />
                                </x-flex>

                                <x-flex>
                                    <x-currency-text-field
                                        v-model="obligor.pivot.sub_limit"
                                        label="Sublimit"
                                        placeholder=" "
                                        :prefix="currencyCode"
                                        :rules="[]"
                                        outline
                                        :optional="true"
                                    />
                                </x-flex>

                                <x-flex class="grow-0">
                                    <x-btn
                                        class="square-icon label-spacing"
                                        color="tertiary"
                                        small
                                        :disabled="selectedObligors.length === 1"
                                        @click="removeObligor(index)"
                                    >
                                        <i class="material-icons-outlined">delete_forever</i>
                                    </x-btn>
                                </x-flex>
                            </x-layout>

                            <x-layout>
                                <x-flex>
                                    <x-btn
                                        color="tertiary"
                                        small
                                        block
                                        @click="addObligor"
                                    >
                                        <i class="material-icons mr-2">add</i>

                                        Add another Obligor
                                    </x-btn>
                                </x-flex>
                            </x-layout>
                        </template>
                    </x-container>
                </x-flex>
            </x-layout>

            <x-layout class="question-set guarantors">
                <x-flex xs2 offset-xs1>
                    <h3 class="question-set-title">Guarantors</h3>
                </x-flex>

                <x-flex xs6 class="question-set-questions">
                    <x-container>
                        <x-layout>
                            <x-flex xs12>
                                <x-radio-group
                                    v-model="showGuarantor"
                                    :row="true"
                                    class="required"
                                    :rules="[
                                        v => ruleRequired(v, 'Guarantor')
                                    ]"
                                >
                                    <template v-slot:label>
                                        Add guarantor?
                                    </template>

                                    <x-radio label="Yes" :value="true" />

                                    <x-radio label="No" :value="false" />
                                </x-radio-group>
                            </x-flex>
                        </x-layout>

                        <template v-if="showGuarantor">
                            <x-layout v-for="(guarantor, index) in selectedGuarantors" :key="index">
                                <x-flex>
                                    <x-autocomplete
                                        v-model="guarantor.country_id"
                                        :items="countries"
                                        class="required"
                                        label="Guarantor country"
                                        placeholder="Country"
                                        item-value="id"
                                        item-text="name"
                                        :rules="[v => ruleRequired(v, 'Guarantor country')]"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                        required
                                        :menu-props="{ offsetY: true }"
                                    />
                                </x-flex>

                                <x-flex>
                                    <x-autocomplete
                                        v-model="guarantor.pivot.guarantor_id"
                                        :items="filteredGuarantors[index]"
                                        class="required"
                                        label="Guarantor"
                                        placeholder="Guarantor"
                                        item-value="id"
                                        item-text="name"
                                        append-icon="keyboard_arrow_down"
                                        outline
                                        :rules="[v => ruleRequired(v, 'Guarantor')]"
                                    />
                                </x-flex>

                                <x-flex class="grow-0">
                                    <x-btn
                                        class="square-icon label-spacing"
                                        color="tertiary"
                                        small
                                        :disabled="selectedGuarantors.length === 1"
                                        @click="removeGuarantor(index)"
                                    >
                                        <i class="material-icons-outlined">delete_forever</i>
                                    </x-btn>
                                </x-flex>
                            </x-layout>

                            <x-layout>
                                <x-flex>
                                    <x-btn
                                        color="tertiary"
                                        small
                                        block
                                        @click="addGuarantor"
                                    >
                                        <i class="material-icons mr-2">add</i>

                                        Add another Guarantor
                                    </x-btn>
                                </x-flex>
                            </x-layout>
                        </template>
                    </x-container>
                </x-flex>
            </x-layout>
        </template>
    </div>
</template>

<script>
import rules from '~/mixins/rules';
import submissionQuoteHelpers from "../../../mixins/submissionQuoteHelpers";

export default {
    name: 'StandalonePolicyQuestions',
    mixins: [rules, submissionQuoteHelpers],
    props: {
        submission: {
            type: Object,
            required: true
        },
        currencies: {
            type: Array,
            required: true
        },
        countries: {
            type: Array,
            required: true
        },
        categories: {
            type: Array,
            required: true
        },
        products: {
            type: Array,
            required: true
        },
        placingBrokerUsers: {
            type: Array,
            default: () => [],
        },
        placingBrokers: {
            type: Array,
            default: () => [],
        },
        clientAssignments: {
            type: Array,
            default: () => [],
        },
        isAmendment: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            insureds: [],
            selectedInsured: null,
            entities: [],
            insuredEntities: [],
            selectedEntities: [],
            selectedEntitiesAddress: [],
            selectedObligors: [],
            selectedGuarantors: [],
            producing_brokers: [],
            placing_brokers: [],
            guarantors: [],
            obligors: [],
            showObligor: null,
            showGuarantor: null,
        }
    },
    computed: {
        currencyCode() {
            const selectedCurrency = this.currencies.find(currency => this.submission.currency_id === currency.id) || {};
            return selectedCurrency.code || '';
        },
        filteredProducts() {
            return this.products.filter(product => product.category_id === this.submission.category_id);
        },
        line1() {
            return this.submission.client_company?.address?.line1 ?? '';
        },
        countryId() {
            return this.submission.broker_address?.country_id;
        }
    },
    async mounted() {
        this.loading = true;
        let mountRequests = [];

        Promise.all([
            axios.get(this.$root.api('entity')),
            axios.get(this.$root.api(`client`))
        ]).then(([entityRes, ClientRes]) => {
            this.entities = entityRes.data.data;
            this.insureds = ClientRes.data.data;
            const submissionEntites = this?.submission.entities?.map(entity => entity.entity_id) || [];
            this.insuredEntities = this.entities.filter(entity => entity.client_company_id === this.selectedInsured);
            this.selectedInsured = this.submission?.client_company_id || null;

            this.$nextTick(() => {
                if (submissionEntites.length > 0) {
                    this.selectedEntities = submissionEntites;
                }
            });
        });

        mountRequests.push(axios.get(this.$root.api('broker')).then(res => {
            this.placing_brokers = res.data.data;

            if (this.placing_brokers.length === 1 && !this.submission.placing_broker_company_id) {
                this.submission.placing_broker_company_id = this.placing_brokers[0].id;
            }
        }));

        mountRequests.push(axios.get(this.$root.api('producing-broker')).then(res => {
            this.producing_brokers = res.data.data;
        }));

        mountRequests.push(axios.get(this.$root.api('obligor')).then(res => {
            this.obligors = res.data.data;
            this.guarantors = res.data.data;

            this.showObligor = this.submission.obligors.length > 0;
            this.showGuarantor = this.submission.guarantors.length > 0;
            this.selectedObligors = this.submission.obligors || [];
            this.selectedGuarantors = this.submission.guarantors || [];
        }));

        await axios.all(mountRequests).then(() => {
            this.loading = false;
        });

        if (!this.submission.broker_address) {
            this.submission.broker_address = {
                country_id: null,
                line1: null
            };
        }

        if (!this.submission.broker_address && this.submission.producing_broker_company_id) {
            const brokerObject = this.producing_brokers.find(broker => broker.id === this.submission.producing_broker_company_id);

            this.submission.broker_address.line1 = brokerObject?.address?.line1 || '';
            this.submission.broker_address.country_id = brokerObject?.address?.country_id || null;
        }
    },
    watch: {
        selectedInsured: {
            handler() {

                let selectedInsured = this.insureds?.find(insured => insured.id == this.selectedInsured);

                this.submission.client_company = selectedInsured ? {
                    ...selectedInsured,
                    address: selectedInsured.address || {}
                } : null;

                this.submission.client_company_id = this.selectedInsured;

                this.insuredEntities = this.entities.filter(entity => entity.client_company_id === this.selectedInsured);
                if (this.selectedEntities.length > 0) {
                    this.selectedEntities = [];
                }

            }
        },
        selectedEntities: {
            handler() {
                let selectedEntitiesAddress = this.entities?.map(entity => (
                    {
                        address: entity.address,
                        selected: false
                    }
                )) || [];

                this.selectedEntities.forEach(entityId => {
                    let entityAddressObject = this.entities?.find(entity => entity.id === entityId)?.address;
                    if (entityAddressObject) {
                        entityAddressObject.selected = true;
                    }

                    return selectedEntitiesAddress.push({
                        entity_id: entityId,
                        address: entityAddressObject || '',
                        name: this.entities.find(entity => entity.id === entityId)?.name,
                        selected: true
                    });
                });
                this.submission.entities = selectedEntitiesAddress.filter(entityAddress => entityAddress.selected === true);
            },
            deep: true
        },
        "submission.producing_broker_company_id"() {
            const brokerObject = this.producing_brokers.find(broker => broker.id === this.submission.producing_broker_company_id);
            this.submission.broker_address.line1 = brokerObject?.address?.line1 || '';
            this.submission.broker_address.country_id = brokerObject?.address?.country_id || null;
        },
        showObligor() {
            if (this.showObligor && this.selectedObligors !== null && this.selectedObligors?.length === 0) {
                this.selectedObligors = [
                    {
                        pivot: {
                            obligor_id: null,
                            sub_limit: null
                        }
                    }
                ];
            }
            if (this.showObligor === false) {
                this.selectedObligors = [];
            }
        },
        selectedObligors: {
            deep: true,
            handler() {
                this.submission.obligors = this.selectedObligors;
            }
        },
        showGuarantor() {
            if (this.showGuarantor && this.selectedGuarantors !== null && this.selectedGuarantors?.length === 0) {
                this.selectedGuarantors = [
                    {
                        pivot: {
                            guarantor_id: null
                        },
                        country_id: null
                    }
                ];
            }

            if (this.showGuarantor === false) {
                this.selectedGuarantors = [];
            }
        },
        selectedGuarantors: {
            deep: true,
            handler() {
                this.submission.guarantors = this.selectedGuarantors;
            }
        }
    },
    methods: {
        addObligor() {
            this.selectedObligors.push({
                pivot: {
                    obligor_id: null,
                    sub_limit: null
                }
            });
        },
        addGuarantor() {
            this.selectedGuarantors.push({
                pivot: {
                    guarantor_id: null
                },
                country_id: null
            });
        },
        removeObligor(index) {
            this.selectedObligors.splice(index, 1);
        },
        removeGuarantor(index) {
            this.selectedGuarantors.splice(index, 1);
        }
    }

}
</script>
